import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Reveal, Overline } from "./Reveal";
import { SERVICES, CONTACT, DURATIONS, BOOKING_DAYS, SLOTS_WEEKDAY, SLOTS_SATURDAY } from "./data";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", service: "", duration: "", weekday: "", time_slot: "", address: "", message: "" };

const darkInput = "bg-white/5 border-white/15 text-white placeholder:text-neutral-500 focus-visible:ring-[var(--pmr-red)] focus-visible:ring-offset-0 h-12 rounded-sm";

export const Booking = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setDay = (v) => setForm((f) => ({ ...f, weekday: v, time_slot: "" }));

  const slots = form.weekday === "Samstag" ? SLOTS_SATURDAY : SLOTS_WEEKDAY;

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "email", "phone", "service", "duration", "weekday", "time_slot", "address"];
    if (required.some((k) => !form[k])) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus – inkl. Dauer, Wunschtag und Uhrzeit.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Anfrage gesendet! Sie erhalten in Kürze eine Bestätigung per E-Mail.");
    } catch (err) {
      toast.error("Etwas ist schiefgelaufen. Bitte rufen Sie mich direkt an.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="relative brushed-metal grain text-white py-24 sm:py-36 overflow-hidden" data-testid="booking">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
        <div className="grid grid-cols-12 gap-10 lg:gap-16">
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <Overline className="text-neutral-400">04 — Termin anfragen</Overline>
              <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl silver-text leading-none">
                Ihr Termin.<br />Ihr Zuhause.
              </h2>
              <p className="mt-6 font-body text-neutral-400 leading-relaxed max-w-md">
                Wählen Sie Ihren Wunschtermin. Ihre Anfrage ist <b className="text-neutral-200">unverbindlich</b> –
                ich bestätige den Termin persönlich per E-Mail oder Telefon.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 space-y-5 font-body">
                <a href={CONTACT.phoneHref} className="flex items-center gap-4 group" data-testid="booking-phone">
                  <span className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center group-hover:border-[var(--pmr-red)] transition-colors text-[var(--pmr-red)]">✆</span>
                  <span className="text-lg group-hover:text-[var(--pmr-red)] transition-colors">{CONTACT.phone}</span>
                </a>
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 group" data-testid="booking-email">
                  <span className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-neutral-300">@</span>
                  <span className="text-neutral-300 group-hover:text-white transition-colors">{CONTACT.email}</span>
                </a>
                <div className="flex items-center gap-4">
                  <span className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-neutral-300">⌂</span>
                  <span className="text-neutral-300">{CONTACT.street}, {CONTACT.city}</span>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-7">
            <Reveal delay={0.1}>
              {done ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                  className="rounded-sm border border-white/10 bg-white/5 p-12 text-center flex flex-col items-center"
                  data-testid="booking-success"
                >
                  <CheckCircle2 size={56} className="text-[var(--pmr-red)]" />
                  <h3 className="mt-6 font-grotesk font-bold text-2xl">Vielen Dank!</h3>
                  <p className="mt-3 font-body text-neutral-400 max-w-sm">
                    Ihre unverbindliche Anfrage ist eingegangen. Eine Zusammenfassung wurde an Ihre
                    E-Mail-Adresse gesendet. Ich bestätige Ihren Wunschtermin schnellstmöglich persönlich.
                  </p>
                  <button onClick={() => setDone(false)} data-testid="booking-reset"
                    className="mt-8 font-grotesk text-sm text-neutral-300 underline underline-offset-4 hover:text-white">
                    Weitere Anfrage senden
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="rounded-sm border border-white/10 bg-white/[0.03] p-7 sm:p-10 space-y-6" data-testid="booking-form">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="Name *"><Input data-testid="booking-name" className={darkInput} value={form.name} onChange={set("name")} placeholder="Ihr Name" /></Field>
                    <Field label="Telefon *"><Input data-testid="booking-phone-input" className={darkInput} value={form.phone} onChange={set("phone")} placeholder="0170 …" /></Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="E-Mail *"><Input data-testid="booking-email-input" type="email" className={darkInput} value={form.email} onChange={set("email")} placeholder="name@mail.de" /></Field>
                    <Field label="Leistung *">
                      <Select value={form.service} onValueChange={set("service")}>
                        <SelectTrigger data-testid="booking-service" className={darkInput}><SelectValue placeholder="Bitte wählen" /></SelectTrigger>
                        <SelectContent>
                          {SERVICES.map((s) => (
                            <SelectItem key={s.id} value={s.title} data-testid={`booking-service-${s.id}`}>{s.title}</SelectItem>
                          ))}
                          <SelectItem value="Beratung / Sonstiges">Beratung / Sonstiges</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <Field label="Behandlungsdauer *">
                    <Select value={form.duration} onValueChange={set("duration")}>
                      <SelectTrigger data-testid="booking-duration" className={darkInput}><SelectValue placeholder="Dauer wählen" /></SelectTrigger>
                      <SelectContent>
                        {DURATIONS.map((d) => (
                          <SelectItem key={d.value} value={d.value} data-testid={`booking-duration-${d.value}`}>{d.label} — {d.price}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="Wunschtag *">
                      <Select value={form.weekday} onValueChange={setDay}>
                        <SelectTrigger data-testid="booking-weekday" className={darkInput}><SelectValue placeholder="Tag wählen" /></SelectTrigger>
                        <SelectContent>
                          {BOOKING_DAYS.map((d) => (
                            <SelectItem key={d} value={d} data-testid={`booking-weekday-${d}`}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Uhrzeit *">
                      <Select value={form.time_slot} onValueChange={set("time_slot")} disabled={!form.weekday}>
                        <SelectTrigger data-testid="booking-time" className={`${darkInput} disabled:opacity-50`}>
                          <SelectValue placeholder={form.weekday ? "Uhrzeit wählen" : "Erst Tag wählen"} />
                        </SelectTrigger>
                        <SelectContent>
                          {slots.map((t) => (
                            <SelectItem key={t} value={t} data-testid={`booking-time-${t}`}>{t} Uhr</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <Field label="Adresse (Hausbesuch) *"><Input data-testid="booking-address" className={darkInput} value={form.address} onChange={set("address")} placeholder="Straße, PLZ, Ort" /></Field>

                  <Field label="Nachricht">
                    <Textarea data-testid="booking-message" value={form.message} onChange={set("message")} placeholder="Beschwerden, Fragen, Verordnung …"
                      className="bg-white/5 border-white/15 text-white placeholder:text-neutral-500 focus-visible:ring-[var(--pmr-red)] focus-visible:ring-offset-0 min-h-28 rounded-sm" />
                  </Field>

                  <button type="submit" disabled={loading} data-testid="booking-submit"
                    className="w-full sm:w-auto font-grotesk font-medium inline-flex items-center justify-center gap-2 rounded-full bg-[var(--pmr-red)] hover:bg-[var(--pmr-red-deep)] disabled:opacity-60 text-white px-8 py-4 transition-colors">
                    {loading ? <><Loader2 size={18} className="animate-spin" /> Wird gesendet…</> : <><Send size={17} /> Unverbindlich anfragen</>}
                  </button>
                  <p className="font-body text-xs text-neutral-500">* Pflichtfelder. Ihre Anfrage ist unverbindlich und wird per E-Mail an Timo Roider gesendet. Die Terminbestätigung erfolgt persönlich.</p>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="font-grotesk text-xs tracking-wide uppercase text-neutral-400">{label}</Label>
    {children}
  </div>
);
